import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import UploadOutlined from "@mui/icons-material/UploadOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { ONTOLOGY_WORKBENCH_STEPS } from "../../data/create-ontology-questions.data";
import {
  DUMMY_UPLOADED_DOCUMENTS,
  GROUND_TO_DOCUMENTS_KPI,
  PROCESSING_FILE,
  RELATIONSHIP_VALIDATION_SUMMARY,
  TOTAL_UPLOADED_DOCUMENTS_COUNT,
  DocumentStatus
} from "../../data/create-ontology-ground-to-documents.data";
import "./CreateOntologyGroundToDocuments.scss";

const STATUS_CLASS: Record<DocumentStatus, string> = {
  Grounded: "create-ground-doc-status--grounded",
  Processing: "create-ground-doc-status--processing"
};

const CreateOntologyGroundToDocuments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;

  const handleBack = () => {
    navigate("/ontology-studio/create/reuse-harmonisation", {
      state: { initiativeId }
    });
  };

  // Next step (Build Knowledge Graph) design not provided yet — return to the dashboard for now.
  const handleContinue = () => {
    navigate("/create-ontology");
  };

  const handleCreateGraph = () => {
    navigate("/ontology-studio/create/build-knowledge-graph", {
      state: { initiativeId }
    });
  };

  return (
    <div className="create-ground-doc-container">
      <nav className="create-ground-doc-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Ground to Documents</span>
      </nav>

      <div className="create-ground-doc-header">
        <h1 className="create-ground-doc-title">Ground to Documents</h1>
        <p className="create-ground-doc-description">
          Upload source evidence, ground concepts and relationships, validate
          mappings, resolve unmatched content, and confirm provenance before the
          knowledge graph is built.
        </p>
        <div className="create-ground-doc-meta">
          <span className="create-ground-doc-meta__pill">
            Initiative ID <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-ground-doc-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-ground-doc-workflow">
        <div className="create-ground-doc-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(0, 3).map((step) => (
            <div
              key={step.id}
              className="create-ground-doc-workflow__step create-ground-doc-workflow__step--done"
            >
              <span className="create-ground-doc-workflow__indicator">✓</span>
              <span className="create-ground-doc-workflow__label">
                {step.label}
              </span>
            </div>
          ))}
        </div>
        <div className="create-ground-doc-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(3, 6).map((step) => (
            <div
              key={step.id}
              className={`create-ground-doc-workflow__step${
                step.id === 5 ? " create-ground-doc-workflow__step--active" : ""
              }${step.id < 5 ? " create-ground-doc-workflow__step--done" : ""}`}
            >
              <span className="create-ground-doc-workflow__indicator">
                {step.id < 5 ? "✓" : step.id === 5 ? "•" : ""}
              </span>
              <span className="create-ground-doc-workflow__label">
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="create-ground-doc-footer">
        <button
          type="button"
          className="create-ground-doc-btn create-ground-doc-btn--secondary"
          onClick={handleBack}
        >
          Back
        </button>
        <div className="create-ground-doc-footer__right">
          <button
            type="button"
            className="create-ground-doc-btn create-ground-doc-btn--secondary"
            onClick={handleContinue}
          >
            Proceed to Relationship Validation
          </button>
          <button
            type="button"
            className="create-ground-doc-btn create-ground-doc-btn--primary"
            onClick={handleCreateGraph}
          >
            Skip &amp; Create Graph &amp; Continue →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOntologyGroundToDocuments;
